<div class="container-fluid dashboardPage py-3">
  <div class="row g-3">

    <!-- -------------------------------------------------------------------- -->
    <!--                            ANCHOR HEADER                             -->
    <!-- -------------------------------------------------------------------- -->
    <div class="col-12">
      <div class="row g-2 align-items-center">

        <div class="col-12 col-lg">
          <h1 class="dashboardPage__title mb-1">Visitas</h1>
          <p class="dashboardPage__subtitle mb-0">
            <span class="dashboardPage__subtitleStrong">{{ model | relativeDay: dashboardTimeZone }}</span>
            <span class="dashboardPage__subtitleDivider d-none d-lg-inline" aria-hidden="true">&middot;</span>
            <span class="d-block d-lg-inline">{{ selectedDateLabel }}</span>
          </p>
        </div>

        <div class="col-12 col-lg-auto">
          <div class="d-flex align-items-center gap-2">

            <label
              for="dashboardDateInput"
              class="visually-hidden">
              Fecha del reporte de visitas
            </label>

            <div class="input-group dashboardPage__dateGroup">
              <button
                class="btn btn-outline-secondary dashboardPage__iconBtn"
                (click)="moveDay(-1)"
                [disabled]="isLoading"
                aria-label="Ver el día anterior"
                title="Día anterior"
                type="button">
                <i class="material-icons" aria-hidden="true">keyboard_arrow_left</i>
              </button>
              <input
                id="dashboardDateInput"
                class="form-control dashboardPage__dateInput"
                placeholder="yyyy-mm-dd"
                name="dp"
                [(ngModel)]="model"
                (dateSelect)="getData()"
                ngbDatepicker
                #d="ngbDatepicker"
              />
              <button
                class="btn btn-outline-secondary dashboardPage__iconBtn"
                (click)="d.toggle()"
                aria-label="Abrir calendario para elegir la fecha"
                title="Abrir calendario"
                type="button">
                <i class="material-icons" aria-hidden="true">calendar_today</i>
              </button>
              <button
                class="btn btn-outline-secondary dashboardPage__iconBtn"
                (click)="moveDay(1)"
                [disabled]="isLoading"
                aria-label="Ver el día siguiente"
                title="Día siguiente"
                type="button">
                <i class="material-icons" aria-hidden="true">keyboard_arrow_right</i>
              </button>
            </div>

            <button
              (click)="getData()"
              type="button"
              [disabled]="isLoading"
              aria-label="Actualizar la información de visitas"
              title="Actualizar"
              class="btn btn-primary dashboardPage__iconBtn dashboardPage__refreshBtn d-flex align-items-center justify-content-center">
              <i class="material-icons" aria-hidden="true">refresh</i>
            </button>

          </div>
        </div>

      </div>
    </div>

    @if (isLoading) {

      <!-- ------------------------------------------------------------------ -->
      <!--                        ANCHOR LOADING SKELETON                     -->
      <!-- ------------------------------------------------------------------ -->
      <div class="col-12 col-lg-4" aria-hidden="true">
        <div class="dashboardPage__card h-100 placeholder-glow">
          <span class="placeholder d-block col-6 rounded mb-3"></span>
          <span class="placeholder d-block col-4 rounded dashboardPage__skeletonKpi"></span>
          <span class="placeholder d-block col-8 rounded"></span>
        </div>
      </div>

      <div class="col-12 col-lg-8" aria-hidden="true">
        <div class="dashboardPage__card h-100 placeholder-glow">
          <span class="placeholder d-block col-4 rounded mb-3"></span>
          <div class="d-flex gap-3">
            <span class="placeholder rounded flex-fill dashboardPage__skeletonType"></span>
            <span class="placeholder rounded flex-fill dashboardPage__skeletonType"></span>
            <span class="placeholder rounded flex-fill dashboardPage__skeletonType"></span>
          </div>
        </div>
      </div>

      <div class="col-12" aria-hidden="true">
        <div class="dashboardPage__card placeholder-glow">
          <span class="placeholder d-block col-3 rounded mb-3"></span>
          <span class="placeholder d-block col-12 rounded dashboardPage__skeletonChart"></span>
        </div>
      </div>

      <div class="visually-hidden" role="status" aria-live="polite">
        Cargando la información de visitas
      </div>

    } @else {

      <!-- ------------------------------------------------------------------ -->
      <!--                          ANCHOR KPI TOTAL                          -->
      <!-- ------------------------------------------------------------------ -->
      <div class="col-12 col-lg-4">
        <div class="dashboardPage__card h-100">
          <p class="dashboardPage__cardLabel mb-1">Total de visitas</p>
          <p class="dashboardPage__kpiValue mb-1">{{ data?.totalVisits ?? 0 }}</p>

          @if (peakHourLabel) {
            <p class="dashboardPage__cardHint mb-0 d-flex align-items-center">
              <i class="material-icons dashboardPage__cardHintIcon" aria-hidden="true">schedule</i>
              <span class="text-truncate">
                Hora pico {{ peakHourLabel }} &middot; {{ peakHourCount }} visitas
              </span>
            </p>
          } @else {
            <p class="dashboardPage__cardHint mb-0">Sin actividad registrada</p>
          }
        </div>
      </div>

      <!-- ------------------------------------------------------------------ -->
      <!--                       ANCHOR KPI BY VISIT TYPE                     -->
      <!-- ------------------------------------------------------------------ -->
      <div class="col-12 col-lg-8">
        <div class="dashboardPage__card h-100">
          <p class="dashboardPage__cardLabel mb-2">Desglose por tipo</p>

          @if (visitTypeSummaries.length) {

            <div class="d-flex align-items-stretch gap-2 gap-lg-3">
              @for (summary of visitTypeSummaries; track summary.name) {
                <div class="dashboardPage__typeItem flex-fill">
                  <span class="dashboardPage__typeHead d-flex align-items-center">
                    <span
                      class="dashboardPage__typeDot"
                      [style.background-color]="summary.color"
                      aria-hidden="true"></span>
                    <span class="dashboardPage__typeName text-truncate" [title]="summary.name">
                      {{ summary.name }}
                    </span>
                  </span>
                  <span class="dashboardPage__typeValue">{{ summary.count }}</span>
                  <span class="dashboardPage__typePercent">{{ summary.percentage }}%</span>
                </div>
              }
            </div>

          } @else {
            <p class="dashboardPage__cardHint mb-0">Sin visitas registradas este día</p>
          }
        </div>
      </div>

      <!-- ------------------------------------------------------------------ -->
      <!--                           ANCHOR CHART                             -->
      <!-- ------------------------------------------------------------------ -->
      <div class="col-12">
        <div class="dashboardPage__card">

          <div class="d-flex align-items-center justify-content-between mb-2">
            <h2 class="dashboardPage__cardTitle mb-0 text-truncate">Visitas por hora</h2>
            <span class="dashboardPage__cardMeta d-none d-lg-flex align-items-center">
              <i class="material-icons dashboardPage__cardHintIcon" aria-hidden="true">public</i>
              Horario local de la colonia (UTC{{ dashboardTimeZone }})
            </span>
          </div>

          @if (hasVisits) {

            <div
              echarts
              [options]="visitsTodaySummary!"
              class="dashboardPage__chart"
              role="img"
              [attr.aria-label]="chartAriaLabel"></div>

            <!-- Alternativa tabular para lectores de pantalla. -->
            <table class="visually-hidden">
              <caption>Visitas por hora del {{ selectedDateLabel }}</caption>
              <thead>
                <tr>
                  <th scope="col">Hora</th>
                  @for (summary of visitTypeSummaries; track summary.name) {
                    <th scope="col">{{ summary.name }}</th>
                  }
                  <th scope="col">Total</th>
                </tr>
              </thead>
              <tbody>
                @for (row of hourlyTableRows; track row.hour) {
                  <tr>
                    <th scope="row">{{ row.hour }}</th>
                    @for (count of row.counts; track $index) {
                      <td>{{ count }}</td>
                    }
                    <td>{{ row.total }}</td>
                  </tr>
                }
              </tbody>
            </table>

          } @else {

            <div class="dashboardPage__emptyState d-flex flex-column align-items-center justify-content-center text-center">
              <i class="material-icons dashboardPage__emptyIcon" aria-hidden="true">event_busy</i>
              <p class="dashboardPage__emptyTitle mb-1">Sin visitas este día</p>
              <p class="dashboardPage__emptyText mb-3">
                No se registraron accesos el {{ selectedDateLabel }}. Elige otra fecha o vuelve a consultar.
              </p>
              <button
                type="button"
                class="btn btn-outline-primary dashboardPage__emptyBtn"
                (click)="getData()">
                Volver a consultar
              </button>
            </div>

          }

        </div>
      </div>

    }

    <!-- -------------------------------------------------------------------- -->
    <!--                     ANCHOR COLONIAS BY STATE MAP                     -->
    <!-- -------------------------------------------------------------------- -->
    <!--
      Se coloca justo debajo de "Visitas por hora", pero FUERA del @if (isLoading)
      de las visitas a propósito: sus datos vienen del NeighborhoodService y no
      dependen de la fecha seleccionada, así que moverse de día no debe hacer
      desaparecer el mapa ni obligar a ECharts a volver a dibujarlo. Además así el
      mapa nunca bloquea al resto del dashboard ni el dashboard bloquea al mapa.
    -->
    @if (isStateMapLoading || stateMapOptions) {

      <div class="col-12">
        <div class="dashboardPage__card">

          <div class="d-flex align-items-center justify-content-between gap-2 mb-2 dashboardPage__mapHeader">
            <h2 class="dashboardPage__cardTitle mb-0 text-truncate">Colonias por estado</h2>

            <div class="d-flex align-items-center dashboardPage__mapTools">
              <span class="dashboardPage__cardMeta d-none d-lg-flex align-items-center">
                <i class="material-icons dashboardPage__cardHintIcon" aria-hidden="true">map</i>
                Color según el % de colonias activas
              </span>

              <!--
                Los controles solo existen cuando ya hay instancia de ECharts a
                la que despacharle acciones. El alto del encabezado queda
                reservado desde el SCSS (__mapHeader), así que aparecer no mueve
                nada de lugar. NO se deshabilitan en los topes de escala: el
                zoom simplemente se topa.
              -->
              @if (!isStateMapLoading) {
                <div
                  class="btn-group dashboardPage__mapZoomGroup"
                  role="group"
                  aria-label="Zoom del mapa">
                  <button
                    class="btn btn-outline-secondary dashboardPage__iconBtn"
                    (click)="zoomStateMap(1)"
                    aria-label="Acercar"
                    title="Acercar"
                    type="button">
                    <i class="material-icons" aria-hidden="true">add</i>
                  </button>
                  <button
                    class="btn btn-outline-secondary dashboardPage__iconBtn"
                    (click)="zoomStateMap(-1)"
                    aria-label="Alejar"
                    title="Alejar"
                    type="button">
                    <i class="material-icons" aria-hidden="true">remove</i>
                  </button>
                  <button
                    class="btn btn-outline-secondary dashboardPage__iconBtn"
                    (click)="resetStateMapView()"
                    aria-label="Restablecer vista"
                    title="Restablecer vista"
                    type="button">
                    <i class="material-icons" aria-hidden="true">restart_alt</i>
                  </button>
                </div>
              }
            </div>
          </div>

          @if (isStateMapLoading) {

            <!-- Skeleton acotado a esta tarjeta: reserva el alto final del mapa
                 para que al llegar el GeoJSON no salte el contenido. -->
            <div class="placeholder-glow" aria-hidden="true">
              <span class="placeholder d-block w-100 rounded dashboardPage__skeletonMap"></span>
            </div>

            <div class="visually-hidden" role="status" aria-live="polite">
              Cargando el mapa de colonias por estado
            </div>

          } @else {

            <!--
              El cursor NO se toca desde el SCSS: zrender ya lo resuelve solo y
              lo escribe como estilo EN LÍNEA sobre su propio contenedor
              (HandlerDomProxy.setCursor en zrender/lib/dom/HandlerProxy.js, a
              partir del cursor del elemento bajo el puntero en
              zrender/lib/Handler.js), así que cualquier regla nuestra sería
              letra muerta sin un !important, que aquí está prohibido.
            -->
            <div
              echarts
              [options]="stateMapOptions!"
              (chartInit)="onStateMapInit($event)"
              class="dashboardPage__map"
              role="img"
              [attr.aria-label]="stateMapAriaLabel"></div>

            <!-- Alternativa tabular para lectores de pantalla y respaldo del
                 color: ni el conteo exacto ni el porcentaje que pinta el mapa
                 dependen solo de la escala de color. -->
            <table class="visually-hidden">
              <caption>Colonias por estado de la República</caption>
              <thead>
                <tr>
                  <th scope="col">Estado</th>
                  <th scope="col">Colonias</th>
                  <th scope="col">Activas</th>
                  <th scope="col">Porcentaje activo</th>
                </tr>
              </thead>
              <tbody>
                @for (summary of stateSummaries; track summary.name) {
                  <tr>
                    <th scope="row">{{ summary.name }}</th>
                    <td>{{ summary.total }}</td>
                    <td>{{ summary.active }}</td>
                    <td>{{ summary.activePercentage }}%</td>
                  </tr>
                }
              </tbody>
            </table>

          }

        </div>
      </div>

    }

    <!-- -------------------------------------------------------------------- -->
    <!--                         ANCHOR CELULARES                             -->
    <!-- -------------------------------------------------------------------- -->
    <!--
      Fuera del @if (isLoading) de las visitas: no depende de la fecha. Cada
      tarjeta tiene su propio estado (loading / ready / empty / error) y se
      pinta en cuanto llega su endpoint, sin esperar a las otras.
    -->
    <div class="col-12 mt-2">
      <div class="d-flex align-items-center justify-content-between gap-2 dashboardPage__sectionHeader">
        <h2 class="dashboardPage__sectionTitle mb-0 text-truncate">Celulares</h2>

        <button
          type="button"
          class="btn btn-outline-primary dashboardPage__devicesToggle d-inline-flex align-items-center"
          [attr.aria-pressed]="includeInactiveDevices"
          (click)="toggleInactiveDevices()">
          <i class="material-icons" aria-hidden="true">{{ includeInactiveDevices ? 'visibility_off' : 'visibility' }}</i>
          <span>{{ includeInactiveDevices ? 'Ocultar no activos' : 'Mostrar no activos' }}</span>
        </button>
      </div>
    </div>

    <!-- ---------------------------- Sistema operativo ---------------------------- -->
    <div class="col-12 col-lg-5">
      <div class="dashboardPage__card h-100">
        <h3 class="dashboardPage__cardTitle mb-2 text-truncate">Sistema operativo</h3>

        @switch (devicesOsState) {

          @case ('loading') {
            <div class="placeholder-glow d-flex align-items-center gap-3" aria-hidden="true">
              <span class="placeholder rounded-circle flex-shrink-0 dashboardPage__skeletonDonut"></span>
              <div class="flex-fill">
                <span class="placeholder d-block col-10 rounded mb-2"></span>
                <span class="placeholder d-block col-8 rounded mb-2"></span>
                <span class="placeholder d-block col-6 rounded"></span>
              </div>
            </div>
            <div class="visually-hidden" role="status" aria-live="polite">
              Cargando celulares por sistema operativo
            </div>
          }

          @case ('ready') {
            <div class="row g-3 align-items-center">
              <div class="col-12 col-sm-6">
                <div
                  echarts
                  [options]="devicesOsOptions!"
                  class="dashboardPage__donut"
                  role="img"
                  [attr.aria-label]="devicesOsAriaLabel"></div>
              </div>

              <div class="col-12 col-sm-6">
                <ul class="list-unstyled mb-0 dashboardPage__shareList">
                  @for (row of devicesOsRows; track row.name) {
                    <li class="d-flex align-items-center dashboardPage__shareItem">
                      <span
                        class="dashboardPage__typeDot"
                        [style.background-color]="row.color"
                        aria-hidden="true"></span>
                      <span class="dashboardPage__shareName text-truncate" [title]="row.name">{{ row.name }}</span>
                      <span class="dashboardPage__shareValue">{{ row.total | number }}</span>
                      <span class="dashboardPage__sharePercent">{{ row.percentage }}%</span>
                    </li>
                  }
                </ul>
              </div>
            </div>
          }

          @case ('empty') {
            <div class="dashboardPage__emptyState dashboardPage__emptyState--compact d-flex flex-column align-items-center justify-content-center text-center">
              <i class="material-icons dashboardPage__emptyIcon" aria-hidden="true">smartphone</i>
              <p class="dashboardPage__emptyTitle mb-1">{{ devicesEmptyTitle }}</p>
              <p class="dashboardPage__emptyText mb-0">{{ devicesEmptyText }}</p>
            </div>
          }

          @case ('error') {
            <div class="dashboardPage__emptyState dashboardPage__emptyState--compact d-flex flex-column align-items-center justify-content-center text-center" role="alert">
              <i class="material-icons dashboardPage__emptyIcon dashboardPage__emptyIcon--error" aria-hidden="true">error_outline</i>
              <p class="dashboardPage__emptyTitle mb-1">No se pudo cargar</p>
              <p class="dashboardPage__emptyText mb-3">Ocurrió un error al consultar los sistemas operativos.</p>
              <button
                type="button"
                class="btn btn-outline-primary dashboardPage__emptyBtn"
                (click)="loadDevicesByOs()">
                Reintentar
              </button>
            </div>
          }

        }
      </div>
    </div>

    <!-- --------------------------------- Marcas ---------------------------------- -->
    <div class="col-12 col-lg-7">
      <div class="dashboardPage__card h-100">
        <h3 class="dashboardPage__cardTitle mb-2 text-truncate">Marcas</h3>

        @switch (devicesBrandState) {

          @case ('loading') {
            <div class="placeholder-glow" aria-hidden="true">
              <span class="placeholder d-block col-11 rounded mb-3 dashboardPage__skeletonBar"></span>
              <span class="placeholder d-block col-8 rounded mb-3 dashboardPage__skeletonBar"></span>
              <span class="placeholder d-block col-6 rounded mb-3 dashboardPage__skeletonBar"></span>
              <span class="placeholder d-block col-4 rounded mb-3 dashboardPage__skeletonBar"></span>
              <span class="placeholder d-block col-3 rounded dashboardPage__skeletonBar"></span>
            </div>
            <div class="visually-hidden" role="status" aria-live="polite">
              Cargando celulares por marca
            </div>
          }

          @case ('ready') {
            <div
              echarts
              [options]="devicesBrandOptions!"
              class="dashboardPage__brandChart"
              [style.height.px]="devicesBrandChartHeight"
              role="img"
              [attr.aria-label]="devicesBrandAriaLabel"></div>

            <!-- Alternativa tabular para lectores de pantalla. -->
            <table class="visually-hidden">
              <caption>Celulares {{ devicesScopeLabel }} por marca</caption>
              <thead>
                <tr>
                  <th scope="col">Marca</th>
                  <th scope="col">Celulares</th>
                  <th scope="col">Porcentaje</th>
                </tr>
              </thead>
              <tbody>
                @for (row of devicesBrandRows; track row.name) {
                  <tr>
                    <th scope="row">{{ row.name }}</th>
                    <td>{{ row.total }}</td>
                    <td>{{ row.percentage }}%</td>
                  </tr>
                }
              </tbody>
            </table>
          }

          @case ('empty') {
            <div class="dashboardPage__emptyState dashboardPage__emptyState--compact d-flex flex-column align-items-center justify-content-center text-center">
              <i class="material-icons dashboardPage__emptyIcon" aria-hidden="true">smartphone</i>
              <p class="dashboardPage__emptyTitle mb-1">{{ devicesEmptyTitle }}</p>
              <p class="dashboardPage__emptyText mb-0">{{ devicesEmptyText }}</p>
            </div>
          }

          @case ('error') {
            <div class="dashboardPage__emptyState dashboardPage__emptyState--compact d-flex flex-column align-items-center justify-content-center text-center" role="alert">
              <i class="material-icons dashboardPage__emptyIcon dashboardPage__emptyIcon--error" aria-hidden="true">error_outline</i>
              <p class="dashboardPage__emptyTitle mb-1">No se pudo cargar</p>
              <p class="dashboardPage__emptyText mb-3">Ocurrió un error al consultar las marcas.</p>
              <button
                type="button"
                class="btn btn-outline-primary dashboardPage__emptyBtn"
                (click)="loadDevicesByBrand()">
                Reintentar
              </button>
            </div>
          }

        }
      </div>
    </div>

    <!-- ---------------------------- Top 10 modelos ------------------------------- -->
    <div class="col-12">
      <div class="dashboardPage__card">
        <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
          <h3 class="dashboardPage__cardTitle mb-0 text-truncate">Top 10 modelos</h3>
          @if (devicesModelState === 'ready') {
            <span class="dashboardPage__cardMeta d-flex align-items-center">
              <i class="material-icons dashboardPage__cardHintIcon" aria-hidden="true">smartphone</i>
              {{ devicesModelTotal | number }} celulares
            </span>
          }
        </div>

        @switch (devicesModelState) {

          @case ('loading') {
            <div class="placeholder-glow" aria-hidden="true">
              @for (item of [1, 2, 3, 4, 5]; track item) {
                <span class="placeholder d-block col-12 rounded mb-2 dashboardPage__skeletonRow"></span>
              }
            </div>
            <div class="visually-hidden" role="status" aria-live="polite">
              Cargando celulares por modelo
            </div>
          }

          @case ('ready') {
            <table class="table table-borderless align-middle mb-0 dashboardPage__modelTable">
              <caption class="visually-hidden">
                Los 10 modelos con más celulares {{ devicesScopeLabel }}
              </caption>
              <thead>
                <tr>
                  <th scope="col" class="dashboardPage__modelRank">#</th>
                  <th scope="col">Modelo</th>
                  <th scope="col" class="d-none d-md-table-cell dashboardPage__modelBarCol">
                    <span class="visually-hidden">Proporción</span>
                  </th>
                  <th scope="col" class="text-end">Celulares</th>
                  <th scope="col" class="text-end">%</th>
                </tr>
              </thead>
              <tbody>
                @for (row of devicesModelRows; track row.name) {
                  <tr [class.dashboardPage__modelOthers]="row.isOthers">
                    <td class="dashboardPage__modelRank">{{ row.rank ?? '' }}</td>
                    <th scope="row" class="dashboardPage__modelName">
                      <span class="d-block text-truncate" [title]="row.name">{{ row.name }}</span>
                      @if (row.brand) {
                        <span class="d-block text-truncate dashboardPage__modelBrand" [title]="row.brand">{{ row.brand }}</span>
                      }
                    </th>
                    <td class="d-none d-md-table-cell dashboardPage__modelBarCol" aria-hidden="true">
                      <span class="d-block dashboardPage__modelTrack">
                        <span class="d-block dashboardPage__modelBar" [style.width.%]="row.barWidth"></span>
                      </span>
                    </td>
                    <td class="text-end dashboardPage__modelValue">{{ row.total | number }}</td>
                    <td class="text-end dashboardPage__sharePercent">{{ row.percentage }}%</td>
                  </tr>
                }
              </tbody>
            </table>
          }

          @case ('empty') {
            <div class="dashboardPage__emptyState dashboardPage__emptyState--compact d-flex flex-column align-items-center justify-content-center text-center">
              <i class="material-icons dashboardPage__emptyIcon" aria-hidden="true">smartphone</i>
              <p class="dashboardPage__emptyTitle mb-1">{{ devicesEmptyTitle }}</p>
              <p class="dashboardPage__emptyText mb-0">{{ devicesEmptyText }}</p>
            </div>
          }

          @case ('error') {
            <div class="dashboardPage__emptyState dashboardPage__emptyState--compact d-flex flex-column align-items-center justify-content-center text-center" role="alert">
              <i class="material-icons dashboardPage__emptyIcon dashboardPage__emptyIcon--error" aria-hidden="true">error_outline</i>
              <p class="dashboardPage__emptyTitle mb-1">No se pudo cargar</p>
              <p class="dashboardPage__emptyText mb-3">Ocurrió un error al consultar los modelos.</p>
              <button
                type="button"
                class="btn btn-outline-primary dashboardPage__emptyBtn"
                (click)="loadDevicesByModel()">
                Reintentar
              </button>
            </div>
          }

        }
      </div>
    </div>

    <!-- -------------------------------- Plegables -------------------------------- -->
    <div class="col-12">
      <div class="dashboardPage__card">
        <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
          <h3 class="dashboardPage__cardTitle mb-0 text-truncate">Plegables</h3>
          <span class="dashboardPage__cardMeta">Solo Android</span>
        </div>

        @switch (devicesFoldableState) {

          @case ('loading') {
            <div class="row g-3 placeholder-glow" aria-hidden="true">
              <div class="col-12 col-md-4">
                <span class="placeholder d-block col-4 rounded dashboardPage__skeletonKpi"></span>
                <span class="placeholder d-block col-8 rounded"></span>
              </div>
              <div class="col-12 col-md-8">
                @for (item of [1, 2, 3]; track item) {
                  <span class="placeholder d-block col-12 rounded mb-2 dashboardPage__skeletonRow"></span>
                }
              </div>
            </div>
            <div class="visually-hidden" role="status" aria-live="polite">
              Cargando celulares plegables
            </div>
          }

          @case ('ready') {
            <div class="row g-3">
              <div class="col-12 col-md-4">
                <p class="dashboardPage__kpiValue mb-1">{{ devicesFoldableTotal | number }}</p>
                <p class="dashboardPage__cardHint mb-0">
                  {{ devicesFoldableTotal === 1 ? 'celular' : 'celulares' }}
                  &middot; {{ devicesFoldablePercentage }}% del total
                  <span class="visually-hidden">
                    de {{ devicesFoldableDevicesTotal | number }} celulares {{ devicesScopeLabel }}
                  </span>
                </p>
              </div>

              <div class="col-12 col-md-8">
                <table class="table table-borderless align-middle mb-0 dashboardPage__modelTable">
                  <caption class="visually-hidden">
                    Modelos plegables entre los celulares {{ devicesScopeLabel }}; el porcentaje es sobre el total de plegables
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Modelo</th>
                      <th scope="col" class="text-end">Celulares</th>
                      <th scope="col" class="text-end">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of devicesFoldableRows; track row.name) {
                      <tr [class.dashboardPage__modelOthers]="row.isOthers">
                        <th scope="row" class="dashboardPage__modelName">
                          <span class="d-block text-truncate" [title]="row.name">{{ row.name }}</span>
                          @if (row.brand) {
                            <span class="d-block text-truncate dashboardPage__modelBrand" [title]="row.brand">{{ row.brand }}</span>
                          }
                        </th>
                        <td class="text-end dashboardPage__modelValue">{{ row.total | number }}</td>
                        <td class="text-end dashboardPage__sharePercent">{{ row.percentage }}%</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          }

          @case ('empty') {
            <div class="dashboardPage__emptyState dashboardPage__emptyState--compact d-flex flex-column align-items-center justify-content-center text-center">
              <i class="material-icons dashboardPage__emptyIcon" aria-hidden="true">smartphone</i>
              <p class="dashboardPage__emptyTitle mb-1">{{ devicesFoldableEmptyTitle }}</p>
              <p class="dashboardPage__emptyText mb-0">{{ devicesFoldableEmptyText }}</p>
            </div>
          }

          @case ('error') {
            <div class="dashboardPage__emptyState dashboardPage__emptyState--compact d-flex flex-column align-items-center justify-content-center text-center" role="alert">
              <i class="material-icons dashboardPage__emptyIcon dashboardPage__emptyIcon--error" aria-hidden="true">error_outline</i>
              <p class="dashboardPage__emptyTitle mb-1">No se pudo cargar</p>
              <p class="dashboardPage__emptyText mb-3">Ocurrió un error al consultar los celulares plegables.</p>
              <button
                type="button"
                class="btn btn-outline-primary dashboardPage__emptyBtn"
                (click)="loadDevicesFoldables()">
                Reintentar
              </button>
            </div>
          }

        }
      </div>
    </div>

  </div>
</div>
